import { css } from 'styled-components'
import { ReactElement } from 'react'
import PlatformHelper from '../PlatformHelper'
import { LayoutRectangleType } from '../elements/DimensionsProvider/LayoutRectangleType'
import {
    FlexboxProps,
    BaseFontProps,
    BasicViewProps,
    GridProps,
    ImageProps,
    ClickableProps,
    InputProps,
} from './types'

import { elevationToBoxShadow } from './utils/Elevation'
import { space } from './properties/space'
import { border, BorderProps } from './properties/border'
import { color } from './properties/color'
import { position } from './properties/position'
import { typography } from './properties/typography'
import { display } from './properties/display'
import { layout } from './properties/layout'

/*
    Text Style
 */
export const text = css<BaseFontProps>`
    ${space}
    ${color}
    ${typography}
    ${display}
    ${(props): string =>
        props.textDecorationLine ? `text-decoration: ${props.textDecorationLine};` : ''}
    ${(props): string =>
        props.textDecorationColor ? `text-decoration-color: ${props.textDecorationColor};` : ''}
    ${(props): string => (props.uppercase ? 'text-transform: uppercase;' : '')}
    ${(props): string =>
        // else not testable on native
        /* istanbul ignore next */
        PlatformHelper.isNative || props.selectable ? '' : 'user-select: none;'}
`

/*
    Flex Style
 */
const getFlexValue = (value?: number): number => value ?? 1

export const flexbox = css<FlexboxProps>`
    ${(props): string => {
        if (props.horizontal) {
            return props.reverse
                ? 'display:flex; flex-direction: row-reverse;'
                : 'display:flex; flex-direction: row;'
        }
        return props.reverse
            ? 'display:flex; flex-direction: column-reverse;'
            : 'display:flex; flex-direction: column;'
    }}
    ${(props): string => {
        const flexGrow = getFlexValue(props.flexGrow)
        const flexBasis = props.flexBasis || (props.noExpand || flexGrow === 0 ? 'auto' : '0')
        return props.noExpand
            ? `flex: 0 ${getFlexValue(props.flexShrink)} ${flexBasis};`
            : `flex: ${flexGrow} ${getFlexValue(props.flexShrink)} ${flexBasis};`
    }}
    ${(props): string => (props.flexWrap ? `flex-wrap: ${props.flexWrap};` : '')}
`

/*
    Border Style
 */
const bordering = css<BorderProps>`
    /* We need this, otherwise there is always a 3px border on web by default */
    border-width: 0px;
    border-style: solid;
    ${border}
`

/*
    Basic View Style
 */
export const basicView = css<BasicViewProps>`
    ${space}
    ${display}
    ${color}
    ${bordering}
    ${(props): string => {
        // onAndroid ist not possible on web and box-shadow not applicable on android
        /* istanbul ignore next */
        if (props.elevation) {
            if (PlatformHelper.onAndroid) {
                return `elevation: ${props.elevation};`
            }
            return `box-shadow: ${elevationToBoxShadow(props.elevation)};`
        }
        return ''
    }}
    ${position}
    ${(props): string => (props.rotate ? `transform: rotate(${props.rotate}deg);` : '')}
    ${layout}
`

/*
    Grid Style
 */
export const grid = css<GridProps>`
    ${flexbox}
    ${(props): string => (props.evenly ? 'justify-content: space-between;' : '')}
    ${(props): string => {
        if (props.alignRight) {
            return props.horizontal ? 'justify-content: flex-end;' : 'align-items: flex-end;'
        }
        return ''
    }}
    ${(props): string => {
        if (props.alignLeft) {
            return props.horizontal ? 'justify-content: flex-start;' : 'align-items: flex-start;'
        }
        return ''
    }}
    ${(props): string => {
        if (props.alignTop) {
            return props.horizontal ? 'align-items: flex-start;' : 'justify-content: flex-start;'
        }
        return ''
    }}
    ${(props): string => {
        if (props.alignBottom) {
            return props.horizontal ? 'align-items: flex-end;' : 'justify-content: flex-end;'
        }
        return ''
    }}
    ${(props): string => (props.center ? 'justify-content: center; align-items: center;' : '')}
    ${(props): string => {
        if (props.centerHorizontal && !props.center) {
            return props.horizontal ? 'justify-content: center;' : 'align-items: center;'
        }
        return ''
    }}
    ${(props): string => {
        if (props.centerVertical && !props.center) {
            return props.horizontal ? 'align-items: center;' : 'justify-content: center;'
        }
        return ''
    }}
    ${basicView}
`

/*
    Image Style
 */
export const image = css<ImageProps>`
    ${color}
    ${layout}
    ${border}
    ${position}
`

/*
    Clickable Style
 */
export const clickable = css<ClickableProps>`
    ${(props): string => {
        /* istanbul ignore next */
        if (PlatformHelper.isNative || props.pointer === undefined) return ''
        /* istanbul ignore next */
        return props.pointer
            ? 'cursor: pointer; user-select: none; -webkit-tap-highlight-color: transparent;'
            : 'cursor: default;'
    }}}
    ${(props): string =>
        /* istanbul ignore next */
        props.pointerEvents && !PlatformHelper.isNative
            ? `pointer-events: ${props.pointerEvents};`
            : ''}
    ${(props): string =>
        /* istanbul ignore next */
        props.touchAction && !PlatformHelper.isNative ? `touch-action: ${props.touchAction};` : ''}
`

/*
    Input Style
 */
export const input = css<InputProps>`
    ${color}
    ${display}
    ${typography}
    ${space}
`

/*
    DimensionsProvider Style
 */
export type DimensionsProviderProps = {
    renderChildren: (arg0?: LayoutRectangleType) => ReactElement | null
    testID?: string
    noExpand?: boolean
}

/*
    ScaledSize Style
 */
export type ScaledSize = {
    width: number
    height: number
    scale: number
    fontScale: number
}
